import { AccountSelector, ActionBar, Button, Select, useUpload } from '@components'
import { ContactSelector } from '@components/uas/ContactSelector'
import { RuleSelector } from '@components/uas/RuleSelector'
import { getSettingsPageUrl, NocfoPlan } from '@constants'
import { PermissionSet, useBusinessContext, useDateRangeContext, usePermission } from '@context'
import { fetchBankIntegrations, IDocument, useCurrentUser } from '@query'
import { formatDate, handleAddingFilter, setTagsInFilter } from '@utils'
import dayjs from 'dayjs'
import React, { useMemo, useState } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { useTranslation } from 'react-i18next'
import { FaFileUpload, FaMagic, FaPlus } from 'react-icons/fa'
import styled from 'styled-components'
import { DocumentFormModal } from './DocumentForm'
import { IFilter, IFilterButton } from '@root/components/ActionBar/FilterBar/FilterSelection'
import { AdvertisementLink } from '@root/components/ActionBar/ActionBar'
import { useQuery } from '@tanstack/react-query'
import { TagSelector } from '@root/containers/TagSelector/TagSelector'
import { useAIDocumentCreateTracker } from '@pages/DocumentPage/hooks/useAIDocumentCreateTracker.tsx'
import { CreateDocumentWithAIModal } from '@pages/DocumentPage/CreateDocumentWithAIModal.tsx'
import { useDocumentRelationRoles } from '@query/hooks/useDocumentRelationRoles'
import { useOpenCreateFromQuery } from '@hooks'

interface Props {
  documents: IDocument[]
  onSearch: (value: string) => void
  onFilter: (filters: any) => void
  uploadFile: (file: File) => Promise<any>
  filters: IFilter[]
}

const LINKING_FILTER_KEYS = ['is_linked', 'relation_role', 'has_due_mismatch'] as const

function isLinkingFilter(filter: IFilter): boolean {
  return LINKING_FILTER_KEYS.some(key => key in filter)
}

function getLinkingFilterSelectValue(filters: IFilter[]): string {
  const hasDueMismatch = filters.find(value => 'has_due_mismatch' in value)?.has_due_mismatch
  const isLinked = filters.find(value => 'is_linked' in value)?.is_linked
  const relationRole = filters.find(value => 'relation_role' in value)?.relation_role

  if (hasDueMismatch === true) return 'due_mismatch'
  if (isLinked === false) return 'unlinked'
  if (isLinked === true) return 'linked'
  return relationRole || ''
}

function applyLinkingFilterChange(filters: IFilter[], value: string): IFilter[] {
  const withoutLinkingFilters = filters.filter(f => !isLinkingFilter(f))

  if (value === 'unlinked') {
    return [...withoutLinkingFilters, { is_linked: false }]
  }
  if (value === 'linked') {
    return [...withoutLinkingFilters, { is_linked: true }]
  }
  if (value === 'due_mismatch') {
    return [...withoutLinkingFilters, { has_due_mismatch: true }]
  }
  if (value) {
    // Role options intentionally send only relation_role. Backend treats that as
    // "documents that play this role in a relation", so is_linked=true is redundant.
    return [...withoutLinkingFilters, { relation_role: value }]
  }
  return withoutLinkingFilters
}

export const DocumentActionBar: React.FC<Props> = ({
  documents,
  onSearch,
  onFilter,
  uploadFile,
  filters
}) => {
  const [t] = useTranslation()
  const user = useCurrentUser()
  const [showCreate, setShowCreate] = useState(false)
  const [showCreateWithAI, setShowCreateWithAI] = useState(false)
  const { startTracking: startAIDocumentCreateTracking } = useAIDocumentCreateTracker()
  const { isActive, selectedRange, showAll: showAllDates } = useDateRangeContext()
  const selectedRangeLabel = useMemo(() => {
    return selectedRange?.key === 'custom'
      ? `${formatDate(selectedRange?.date_from)} - ${formatDate(selectedRange?.date_to)}`
      : t('components.dateRangeFilter.' + selectedRange?.key)
  }, [t, selectedRange])
  const { slug, data: business } = useBusinessContext()
  const hasEditPermissions = usePermission(PermissionSet.BOOKKEEPING_EDIT)
  const canManageIntegrations = usePermission(PermissionSet.INTEGRATION_ADMIN)

  const { data: bankIntegrations, isLoading } = useQuery({
    queryKey: [slug, 'bank_integrations'],
    queryFn: () => fetchBankIntegrations({ slug }),
    enabled: canManageIntegrations
  })

  const hasBankIntegration =
    bankIntegrations?.results?.length > 0 || business?.holvi_nocfo_connection_enabled

  const isAdvertisementLinkEnabled =
    canManageIntegrations &&
    business?.subscription_plan === NocfoPlan.FREE &&
    !hasBankIntegration &&
    !isLoading

  useHotkeys(
    'n',
    e => {
      if (!hasEditPermissions) {
        return
      }
      setShowCreate(true)
      e.preventDefault()
    },
    [hasEditPermissions]
  )

  useOpenCreateFromQuery(() => setShowCreate(true), hasEditPermissions)

  const { open, getInputProps } = useUpload(uploadFile, true, ['.csv'])

  const initialDate = documents[0]?.date || dayjs().format('YYYY-MM-DD')
  const { data: relationRoles } = useDocumentRelationRoles()

  const linkingFilterValue = getLinkingFilterSelectValue(filters)

  const filterButtons = React.useMemo<IFilterButton[]>(
    () => [
      {
        id: 'is_sales',
        name: t('document.filter.drafts'),
        filter: { is_draft: true }
      },
      {
        id: 'is_flagged',
        name: t('document.filter.flagged'),
        filter: { is_flagged: true }
      },
      {
        id: 'bank_mismatch',
        name: t('document.filter.bankMismatch'),
        filter: { has_bank_mismatch: true }
      },
      {
        id: 'missing_attachment',
        name: t('document.filter.missingAttachment'),
        filter: { missing_attachment: true }
      },
      {
        id: 'attachment_exempt',
        name: t('document.filter.attachmentExempt'),
        filter: { attachment_exempt: true }
      },
      {
        id: 'income',
        name: t('document.filter.income'),
        filter: { income: true }
      },
      {
        id: 'expense',
        name: t('document.filter.expense'),
        filter: { expense: true }
      },
      {
        id: 'tagged',
        name: t('document.filter.tagged'),
        filter: { tagged: user?.data?.id }
      },
      {
        id: 'has_rule',
        name: t('document.filter.hasRule'),
        filter: { has_rule: true }
      }
    ],
    [t, user?.data?.id]
  )

  const filterInputs = React.useMemo(
    () => [
      <AccountSelector
        value={filters.filter(value => 'account' in value).map(value => value.account)[0]}
        ref={null}
        required={false}
        accountFilter={{
          is_used: true
        }}
        label={t('document.filter.account.label')}
        info={t('document.filter.account.info')}
        placeholder={t('document.filter.account.placeholder')}
        id={'account_search'}
        key={'account_search'}
        onChange={accountId => {
          const updatedFilters = handleAddingFilter(filters, { account: accountId }, 'account')
          onFilter(updatedFilters)
        }}
      />,
      <ContactSelector
        value={filters.filter(value => 'contact' in value).map(value => value.contact)[0]}
        ref={null}
        required={false}
        accountFilter={{}}
        info={t('document.filter.contact.info')}
        label={t('document.filter.contact.label')}
        placeholder={t('document.filter.contact.placeholder')}
        noDataMessage={t('document.filter.contact.noData')}
        id={'contact_search'}
        key={'contact_search'}
        onChange={contactId => {
          const updatedFilters = handleAddingFilter(filters, { contact: contactId }, 'contact')
          onFilter(updatedFilters)
        }}
        fetchOnlyInvoicableContacts={false}
      />,
      <RuleSelector
        value={filters.filter(f => 'rule' in f).map(f => f.rule)[0]}
        ref={null}
        required={false}
        label={t('document.filter.rule.label')}
        info={t('document.filter.rule.info')}
        placeholder={t('document.filter.rule.placeholder')}
        id={'rule_search'}
        key={'rule_search'}
        onChange={ruleId => {
          const updatedFilters = handleAddingFilter(filters, { rule: ruleId }, 'rule')
          onFilter(updatedFilters)
        }}
      />,
      <Select
        key="linking_filter"
        name="linking_filter"
        label={t('document.filter.linking.label')}
        info={t('document.filter.linking.info')}
        required={false}
        value={linkingFilterValue}
        onChange={event => {
          onFilter(applyLinkingFilterChange(filters, event.target.value))
        }}
      >
        <option value="">{t('document.filter.linking.all')}</option>
        <optgroup label={t('document.filter.linking.status')}>
          <option value="unlinked">{t('document.filter.linking.unlinked')}</option>
          <option value="linked">{t('document.filter.linking.linked')}</option>
          <option value="due_mismatch">{t('document.filter.dueMismatch')}</option>
        </optgroup>
        <optgroup label={t('document.filter.linking.role')}>
          {(relationRoles ?? []).map(role => (
            <option key={role.value} value={role.value}>
              {role.label}
            </option>
          ))}
        </optgroup>
      </Select>,
      ...(business?.has_tags
        ? [
            <TagSelector
              label={t('components.tagFilterSelector.label')}
              key={'tags_filter_selector'}
              value={filters.filter(value => 'tags' in value).map(value => value.tags)[0]}
              onChange={(tagIds: number[]) => {
                const updatedFilters = setTagsInFilter(filters, tagIds)
                onFilter(updatedFilters)
              }}
            />
          ]
        : [])
    ],
    [business?.has_tags, filters, linkingFilterValue, onFilter, relationRoles, t]
  )

  const primaryButtons = [
    <Button
      key="add-btn"
      icon={<FaPlus />}
      onClick={() => setShowCreate(true)}
      data-test={'new-document-button'}
    >
      {t('document.addNew')}
    </Button>,
    <Button
      key="create-with-ai-btn"
      icon={<FaMagic />}
      onClick={() => setShowCreateWithAI(true)}
      data-test="create-document-with-ai-button"
    >
      {t('document.createWithAI.button')}
    </Button>,
    <Button key="csv-import-btn" icon={<FaFileUpload />} onClick={() => open()}>
      {t('document.csvImport')}
    </Button>
  ]

  return (
    <>
      <input {...getInputProps()} />
      <ActionBar
        searchPlaceholder={t('document.searchPlaceholder')}
        onSearch={search => onSearch(search)}
        onFilter={filters => onFilter(filters)}
        filterButtons={filterButtons}
        filterInputs={filterInputs}
        activeFilters={filters}
        primaryButtons={hasEditPermissions && primaryButtons}
        isLast={!isActive}
        additionalContent={
          isAdvertisementLinkEnabled && [
            <AdvertisementLink
              key="advertisement-link"
              text={t('document.advertisingLink')}
              link={getSettingsPageUrl(slug, { page: 'integrations' })}
            />
          ]
        }
      />
      {isActive && (
        <FilterInfo>
          <span className="label">
            {t('document.documentsShown', { timePeriod: selectedRangeLabel })}
          </span>
          <a href="#" onClick={() => showAllDates()} style={{ fontWeight: 'normal' }}>
            {t('document.showAll')}
          </a>
        </FilterInfo>
      )}

      <DocumentFormModal
        isVisible={showCreate}
        onClose={() => setShowCreate(false)}
        defaults={{ date: initialDate }}
        enableCache={true}
      />
      <CreateDocumentWithAIModal
        isVisible={showCreateWithAI}
        onClose={() => setShowCreateWithAI(false)}
        onCreateStarted={({ jobIds }) => startAIDocumentCreateTracking({ jobIds })}
      />
    </>
  )
}

const FilterInfo = styled.div`
  display: flex;
  justify-content: space-between;
  background: ${({ theme }) => theme.colors.neutralWhite}77;
  border-top: 1px solid ${({ theme }) => theme.colors.neutralGray};
  border-bottom-left-radius: ${({ theme }) => theme.borderRadius.md}rem;
  border-bottom-right-radius: ${({ theme }) => theme.borderRadius.md}rem;
  padding: ${({ theme }) => theme.spacing.xs}rem ${({ theme }) => theme.spacing.md}rem;
  font-weight: 500;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.metalGray};
  margin-bottom: 1rem;

  .label {
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.xxs}rem;

    svg {
      margin-top: -2px;
    }
  }
`
